/**
 * シーンの「サイズと位置」(映像を縮小して画面のどこに置くか) の正本。
 * docs/features/video-editor-scene-placement-plan.md。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出し)
 * - video-editor リポジトリの src/schema/placement.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/placement.py (AI 提案の正規化・合成フレーム・要約)
 *
 * ## 意味
 * - `scale`: 枠の大きさ。**出力の幅・高さに対する %** (出力と同じ比率の枠)。10〜100・0.1 刻み。
 *   **縮小だけ** (拡大はクロップ = 静止 / 動き = 時間変化 に任せる。同じ絵を作る二重の手段を作らない)。
 * - `x` / `y`: 余白の**配分** (0〜100・0.1 刻み)。0 = 左 (上) 端に寄せる / 50 = 中央 / 100 = 右 (下) 端に寄せる
 *   (CSS の object-position の % と同じ意味)。倍率を変えても意味が変わらず、はみ出す値は作れない。
 * - 省略 = 配置なし (画面いっぱい)。余白は既存の余白の色 (schema/backgroundColor.ts) で塗る。
 *
 * クロップ (素材のどこを使うか・素材に対する %) とは掛かる場所が違い、両方を組み合わせられる
 * (切り出した部分を縮小して置く)。
 *
 * ## 表示
 * 出力サイズの箱を 1 枚足し、`translate(left%, top%) scale(s)`・`transform-origin: 0 0`・
 * `overflow: hidden` を掛ける (translate の % は箱 = 出力のサイズ基準)。中の層 (クロップの px 計算・
 * オーバーレイの rect・motion) は出力サイズ前提のまま変えない。
 * 層の位置は余白の色 / 映像エフェクト (揺れ) の**内側**、look (色補正) / motion の**外側**で、
 * シーンのオーバーレイは枠の**内側** (ぼかし / 焼き込み文字の隠しが映像とずれないように)。
 */
import { z } from 'zod';

/** scale (出力に対する %) の範囲。100 = 配置なしと同じ。 */
export const PLACEMENT_SCALE_MIN = 10;
export const PLACEMENT_SCALE_MAX = 100;
/** x / y (余白の配分 %) の範囲と既定 (中央)。 */
export const PLACEMENT_POSITION_MIN = 0;
export const PLACEMENT_POSITION_MAX = 100;
export const PLACEMENT_POSITION_DEFAULT = 50;

/**
 * 保存形。**範囲をスキーマで縛らない** (壊れた JSON でプロジェクトごと開けなくならないよう、
 * 範囲のクランプと丸めは normalizePlacement が使うたびに行う。crop と同じ方針)。
 */
export const PlacementSchema = z.object({
  scale: z.number(),
  x: z.number(),
  y: z.number(),
});
export type Placement = z.infer<typeof PlacementSchema>;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 0.1 刻みへの half-up 丸め (backend の placement.py と同じ式)。 */
function roundStep(value: number): number {
  return Math.floor(value * 10 + 0.5) / 10;
}

/** 小数 4 桁への half-up 丸め。-0 は 0 に正規化する (crop.ts と同じ理由)。 */
function round4(value: number): number {
  const rounded = Math.floor(value * 10000 + 0.5) / 10000;
  return rounded === 0 ? 0 : rounded;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/**
 * 外部入力 (AI の提案・古い保存データ・UI の数値) を保存形へ正規化する。
 * **保存値 = 実効値**にするため、範囲へクランプして 0.1 刻みに丸めてから、scale が 100
 * (= 何も縮めていない) なら undefined に倒す。scale が数値でなければ undefined、
 * x / y が数値でなければ中央 (50) を補う。
 */
export function normalizePlacement(value: unknown): Placement | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const rawScale = finiteNumber(raw.scale);
  if (rawScale === null) return undefined;
  const scale = roundStep(clamp(rawScale, PLACEMENT_SCALE_MIN, PLACEMENT_SCALE_MAX));
  if (scale >= PLACEMENT_SCALE_MAX) return undefined;
  const position = (v: unknown) =>
    roundStep(clamp(finiteNumber(v) ?? PLACEMENT_POSITION_DEFAULT, PLACEMENT_POSITION_MIN, PLACEMENT_POSITION_MAX));
  return { scale, x: position(raw.x), y: position(raw.y) };
}

export interface PlacementBox {
  /** 枠の左上 (出力幅 / 高さに対する %)。 */
  leftPercent: number;
  topPercent: number;
  /** 枠の大きさ (出力に対する割合。0.1〜1)。 */
  scale: number;
}

/**
 * 枠の位置と大きさ (3 実装で同値)。
 *
 * ```
 * left = (100 - scale) × x / 100
 * top  = (100 - scale) × y / 100
 * ```
 * 正規化してから計算するので、壊れた保存値でも枠は画面の内側に収まる。配置なしなら null。
 */
export function placementBox(value: unknown): PlacementBox | null {
  const placement = normalizePlacement(value);
  if (!placement) return null;
  const free = PLACEMENT_SCALE_MAX - placement.scale;
  return {
    leftPercent: round4((free * placement.x) / 100),
    topPercent: round4((free * placement.y) / 100),
    scale: round4(placement.scale / 100),
  };
}

/** CSS の transform 文字列 (transform-origin は `0 0` で使う)。 */
export function placementTransformCss(box: PlacementBox): string {
  return `translate(${box.leftPercent}%, ${box.topPercent}%) scale(${box.scale})`;
}

/** UI / 要約 / README に出す 1 行 (backend の describe_placement と同じ文言)。 */
export function placementLabel(placement: Placement | null | undefined): string {
  if (!placement) return 'なし';
  return `${placement.scale}% (左右 ${placement.x}% / 上下 ${placement.y}%)`;
}
